import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import iconFileDownload from "../../../assets/media/icon-file-download.svg";
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable, } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import Navbar from "../../navbar";
import Header from "../../header";
import Pagination from "../dashboard/appPagination";
import RejectDialog from "./rejectDialog/rejectDialog";
import StatusConfirmDialog, {
  STATUS_LABELS,
  getStatusRoute,
} from "./statusConfirmDialog/statusConfirmDialog";
import AuditTrailModal from "./auditTrail/AuditTrailModal";
import { fetchApplications, updateApplication, clearApplications, isThunkFulfilled } from "../../../store/reducers/applicationSlice";
import { createNotification } from "../../../store/reducers/notificationSlice";
import { handleFilesDownload } from "../../../store/reducers/fileSlice";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import iconSearch from "../../../assets/media/icon-search.svg";
import iconMenuDots from "../../../assets/media/icon-menu-dots.svg";
import {
  buildLifecycleDateParams,
  formatLifecycleDate,
  getSubmissionDate,
} from "../../../utility/lifecycleDates";

const columnHelper = createColumnHelper();

const AppDiscrepancies = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const authToken = user.access_token;

  const applications = useSelector((state) => state.applications);
  const pagination = useSelector((state) => state.applications.pagination);
  const applicationLoading = useSelector((state) => state.applications.fetchLoading);
  const getallUserdata = useSelector((state) => state.userdata.getalluserdata);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchBy, setSearchBy] = useState("applicationId");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filteredApplication, setFilteredApplication] = useState([]);
  const [downloadingIds, setDownloadingIds] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedRejectItem, setSelectedRejectItem] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalNote, setModalNote] = useState("");
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [auditApplication, setAuditApplication] = useState(null);

  const handleOpenAuditTrail = (item) => {
    setAuditApplication(item || null);
    setShowAuditTrail(true);
  };

  const handleCloseAuditTrail = () => {
    setShowAuditTrail(false);
    setAuditApplication(null);
  };

  // Clear applications when component mounts to ensure fresh data
  useEffect(() => {
    dispatch(clearApplications());
  }, [dispatch]);

  // Fetch applications when date filter changes
  useEffect(() => {
    // Ensure itemsPerPage is a valid positive number
    const validItemsPerPage = (typeof itemsPerPage === 'number' && itemsPerPage > 0) ? itemsPerPage : 10;

    let queryParams = {
      page: currentPage,
      limit: validItemsPerPage,
      state: ['discrepancies', 'descrepency_GB_Company', 'descrepency_GB_Employee', 'descrepency_GB_GB']
    };

    if (searchQuery && searchQuery.trim() !== '') {
      queryParams[searchBy] = searchQuery.trim();
    }

    Object.assign(
      queryParams,
      buildLifecycleDateParams("submitted", startDate, endDate)
    );

    dispatch(fetchApplications({ accessToken: authToken, queryParams }));
  }, [authToken, dispatch, startDate, endDate, currentPage, searchQuery, itemsPerPage, searchBy]);

  // Merge user data and filter applications
  useEffect(() => {
    const applicationsArray = Array.isArray(applications.data)
      ? applications.data
      : applications.data?.applications || applications.data?.data || [];

    if (applicationsArray.length > 0) {
      const mergedData = applicationsArray.map((appItem) => {
        const userItem = getallUserdata?.find?.(
          (userData) => userData.id === appItem.user_id
        );
        if (userItem) {
          return {
            ...appItem,
            applicationId: appItem.id,
            ...userItem,
            userId: userItem.id,
          };
        }
        return { ...appItem, applicationId: appItem.id };
      });

      // Since we're using server-side filtering, we only need to filter by company_id
      const filteredData = mergedData.filter(
        (item) => (item.company_id === user.user.company_id || user.user.is_admin || ["reviewer", "viewer"].includes(user.user.user_role?.toLowerCase()))
      );

      setFilteredApplication(filteredData);
    } else {
      setFilteredApplication([]);
    }
  }, [applications.data, getallUserdata, user.user.company_id, user.user.is_admin]);

  const handleSearchInputChange = (e) => {
    let value = e.target.value;
    if (searchBy === "applicationId") {
      value = value.replace(/^gb-?/i, "").replace(/\D/g, "");
    }
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleSearchByChange = (e) => {
    setSearchBy(e.target.value);
    setSearchQuery("");
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing items per page
  };

  const getTotalPages = () => {
    // Use server-side pagination metadata
    if (pagination?.totalPages) {
      return pagination.totalPages;
    }
    // Fallback to client-side calculation
    const validItemsPerPage = (typeof itemsPerPage === 'number' && itemsPerPage > 0) ? itemsPerPage : 10;
    return Math.ceil(filteredApplication.length / validItemsPerPage);
  };

  const handleDownload = useCallback(async (item) => {
    const { applicationId, userId } = item;
    const id = applicationId || item.id;

    // Prevent multiple downloads - check if already downloading
    if (downloadingIds.includes(id)) {
      return;
    }

    setDownloadingIds((prev) => [...prev, id]);

    try {
      await handleFilesDownload({ userId, applicationId: id, authToken });
    } catch (error) {
      console.error("Download failed", error);
    } finally {
      setDownloadingIds((prev) => prev.filter((downloadId) => downloadId !== id));
    }
  }, [authToken, downloadingIds]);

  const handleOpenReject = (item) => {
    setSelectedRejectItem(item);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const handleCloseReject = () => {
    setShowRejectModal(false);
    setSelectedRejectItem(null);
    setRejectReason("");
  };

  const requestStatusChange = (item, state, discrepancies = null) => {
    setPendingStatusChange({ item, state, discrepancies });
    setShowStatusModal(true);
  };

  const handleCloseStatusModal = () => {
    setShowStatusModal(false);
    setPendingStatusChange(null);
  };

  const handleConfirmStatusChange = (remarks = "") => {
    if (!pendingStatusChange) return;
    const { item, state, discrepancies } = pendingStatusChange;
    handleCloseStatusModal();
    handleChangeStatus(
      { ...item, remarks: remarks || undefined },
      state,
      discrepancies
    );
  };

  const handleConfirmReject = (remarks = "") => {
    if (!selectedRejectItem || !remarks.trim()) return;
    const item = selectedRejectItem;
    handleCloseReject();
    handleChangeStatus({ ...item, remarks: remarks.trim() }, 5, null);
  };

  const handleChangeStatus = async (item, state, discrepancies) => {
    const result = await dispatch(
      updateApplication({
        id: item?.applicationId,
        state: state,
        descripencie: discrepancies || item?.discrepancies,
        remarks: item?.remarks || undefined,
        accessToken: authToken,
      })
    );
    if (!isThunkFulfilled(result)) return;

    const validItemsPerPage = (typeof itemsPerPage === 'number' && itemsPerPage > 0) ? itemsPerPage : 10;

    let queryParams = {
      page: currentPage,
      limit: validItemsPerPage,
      search: searchQuery,
      state: ['descrepency_GB_GB', 'descrepency_GB_Company', 'descrepency_GB_Employee', 'discrepancies']
    };

    if (startDate && endDate) {
      queryParams.start_date = startDate.toISOString();
      queryParams.end_date = endDate.toISOString();
    }

    dispatch(fetchApplications({ accessToken: authToken, queryParams }));

    let statusText = '';
    if (state === 1 || state === 'in_process') statusText = 'In Process';
    else if (state === 4 || state === 'approved') statusText = 'Approved';
    else if (state === 5 || state === 'rejected') statusText = 'Rejected';
    else if (state === 20) statusText = 'Assigned to Company';
    else if (state === 'descrepency_GB_GB' || state === 2) statusText = 'Discrepancies';
    else statusText = 'Updated';

    const appName = item.name || item.applicationName || 'Application';
    const appId = item.applicationId || item.id;
    const subject = `Application Status Update: ${appName} (GB-${appId})`;
    const htmlContent = `
        <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
          <h2 style="color: #2c3e50;">Application Status Update</h2>
          <p>Dear User,</p>
          <p>Your application <strong>${appName}</strong> (ID: <strong>GB-${appId}</strong>) has been added in Discrepancy GB, status updated.</p>
          <p><strong>New Status:</strong> <span style="color: #007bff; font-weight: bold;">${statusText}</span></p>
          <p>Please log in to your dashboard to view more details.</p>
          <br>
          <p>Best regards,</p>
          <p><strong>Gaming Board Team</strong></p>
        </div>
      `;

    dispatch(createNotification({
      message: `Your application ${appName} (GB-${appId}) status has been updated to ${statusText}.`,
      userId: item.userId || item.user_id,
      subject: subject,
      htmlMailContent: htmlContent,
      sendToCompanyAdmins: true,
      accessToken: authToken,
      is_admin: false,
    }));

    const destination = getStatusRoute(state);
    if (destination) {
      navigate(destination);
    } else {
      dispatch(fetchApplications({ accessToken: authToken, queryParams }));
    }
  };

  // Use server-side pagination - no need for client-side slicing
  const paginatedData = useMemo(() => {
    return filteredApplication
      .sort((a, b) => b.applicationId - a.applicationId);
  }, [filteredApplication]);

  // React Table columns
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: "S.NO.",
        cell: (info) => (currentPage - 1) * itemsPerPage + info.row.index + 1,
      }),
      columnHelper.accessor("applicationId", {
        header: "Application No.",
        cell: (info) => `GB-${info.getValue()}`,
      }),
      columnHelper.display({
        id: "applicationName",
        header: "Application Name",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong title={row.name}>
                {row.name?.length > 50
                  ? `${row.name.substring(0, 50)}...`
                  : row.name}
              </strong>
              <span>{row.categoryName}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedBy",
        header: "Submitted By",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.companyName || row.company_name || 'N/A'}</strong>
              <span>{row.companyEmail || row.company_email || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedFor",
        header: "Submitted For",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.userName || row.user_name || 'N/A'}</strong>
              <span>{row.userEmail || row.user_email || row.email || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submissionDate",
        header: "Submitted Date",
        cell: (info) => (
          <span className="dateText">
            {formatLifecycleDate(getSubmissionDate(info.row.original))}
          </span>
        ),
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => {
          const row = info.row.original;
          return <span>{row.state === 'descrepency_GB_GB' ? 'Assigned to GB' : row.state === 'discrepancies' ? 'Discrepancies' : row.state === 'descrepency_GB_Company' ? 'Assigned to Company' : 'Assigned to Employee'}</span>;

        },
      }),
      // columnHelper.accessor("note", {
      //   header: "Note",
      //   cell: (info) => {
      //     const row = info.row.original;
      //     const note =
      //       row.discrepancy &&
      //         row.discrepancy.dis_form2_1 &&
      //         row.discrepancy.dis_form2_1.value
      //         ? row.discrepancy.dis_form2_1.value
      //         : "";

      //     if (!note) {
      //       return <span className="dateText" style={{ color: "#888" }}>No note</span>;
      //     }

      //     return (
      //       <span
      //         className="dateText"
      //         style={{ color: "#007bff", cursor: "pointer", textDecoration: "none" }}
      //         onClick={() => {
      //           setModalNote(note);
      //           setShowModal(true);
      //         }}
      //       >
      //         {note.length > 30 ? note.slice(0, 30) + "..." : note}
      //       </span>
      //     );
      //   },
      // }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardActions">
              <div className="dropdown d-inline-flex">
                <button
                  className="application-dropdown dropdown-toggle border-0 bg-transparent"
                  type="button"
                  id="dropdownMenuButton"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img src={iconMenuDots} alt="Menu Dots" />
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="dropdownMenuButton"
                >
                  <li>
                    {downloadingIds.includes(row.applicationId || row.id) ? (
                      <span className="dropdown-item disabled" style={{ cursor: 'not-allowed', opacity: 0.6, pointerEvents: 'none' }}>
                        Downloading...
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const id = row.applicationId || row.id;
                          if (!downloadingIds.includes(id)) {
                            handleDownload(row);
                          }
                        }}
                        style={{ cursor: 'pointer', border: 'none', background: 'transparent', width: '100%', textAlign: 'left', padding: '0 16px' }}
                      >
                        Download
                      </button>
                    )}
                  </li>
                  <li>
                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={() => handleOpenAuditTrail(row)}
                    >
                      Audit Trail
                    </button>
                  </li>
                  {row.state !== 'descrepency_GB_Company' && row.state !== 'descrepency_GB_Employee' && (
                    <>
                      <li>
                        <Link
                          to={`/descrip-applications/${row.applicationId}`}
                          className="dropdown-item"
                        >
                          View and Status
                        </Link>
                      </li>
                      <li>
                        <Link
                          onClick={() => requestStatusChange(row, 1)}
                          className="dropdown-item"
                        >
                          Move to In process
                        </Link>
                      </li>
                      <li>
                        <Link
                          onClick={() => requestStatusChange(row, 4)}
                          className="dropdown-item"
                        >
                          Move to Approved
                        </Link>
                      </li>
                      <li>
                        <Link
                          onClick={() => handleOpenReject(row)}
                          className="dropdown-item"
                        >
                          Move to Rejected
                        </Link>
                      </li>
                      <li>
                        {(row.state === 'descrepency_GB_GB' || row.state === 'discrepancies') && (
                          <Link
                            onClick={() => requestStatusChange(row, 20)}
                            className="dropdown-item"
                          >
                            Assign to company
                          </Link>
                        )}
                      </li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          );
        },
      }),
    ],
    [downloadingIds, navigate, getallUserdata, handleDownload, currentPage, itemsPerPage]
  );

  const table = useReactTable({
    data: Array.isArray(paginatedData) ? paginatedData : [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="mainApp">
      <Navbar />
      <Header />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          {/* Header with search + date filter */}
          <div className="boardHeader">
            <div className="boardHeadLeft">
              <h2>Discrepancies</h2>
            </div>
            <div className="boardHeadRight">
              <div className="">
                <select onChange={handleSearchByChange} value={searchBy}>
                  <option value="applicationId">Search by Application ID</option>
                  <option value="companyName">Search by Company Name</option>
                  <option value="username">Search by Employee Name</option>
                  {/* <option value="email">Search by Email </option> */}
                </select>
              </div>
              <div className="searchForm">
                <input
                  type="text"
                  placeholder={searchBy === "applicationId" ? "GB-XXX" : "Search"}
                  onChange={handleSearchInputChange}
                  value={searchBy === "applicationId" && searchQuery ? `GB-${searchQuery}` : searchQuery}
                  className="searchInput"
                />
                <button type="button" className="searchBtn">
                  <img src={iconSearch} alt="search" />
                </button>
              </div>
              <div className="datePicker">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    const [start, end] = update;
                    setStartDate(start);
                    setEndDate(end);
                    setCurrentPage(1);
                  }}
                  isClearable={true}
                  placeholderText="Select Date Range"
                  className="date-range-input"
                  dateFormat="yyyy-MM-dd"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="boardContent table-responsive" style={{ position: 'relative' }}>
            {/* Loader overlay for table section */}
            {applicationLoading && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 10,
                  borderRadius: '4px'
                }}
              >
                <div className="text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 mb-0">Loading applications...</p>
                </div>
              </div>
            )}
            <table className="table">
              <thead className="">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr className="" key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {applicationLoading ? (
                  <tr>
                    <td colSpan={table.getHeaderGroups()?.[0]?.headers.length} style={{ textAlign: "center", padding: "20px" }}>
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr className="" key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={table.getHeaderGroups()?.[0]?.headers.length} style={{ textAlign: "center", padding: "20px" }}>
                      {applicationLoading ? "Loading..." : "Result not found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <RejectDialog
            open={showRejectModal}
            reason={rejectReason}
            setReason={setRejectReason}
            onReject={handleConfirmReject}
            onCancel={handleCloseReject}
          />

          <StatusConfirmDialog
            open={showStatusModal}
            title="Are you sure you want to change status?"
            message={
              pendingStatusChange
                ? `Change status to "${STATUS_LABELS[pendingStatusChange.state] || pendingStatusChange.state}"?`
                : ""
            }
            onConfirm={handleConfirmStatusChange}
            onCancel={handleCloseStatusModal}
          />

          <AuditTrailModal
            open={showAuditTrail}
            onClose={handleCloseAuditTrail}
            applicationId={auditApplication?.applicationId || auditApplication?.id}
            applicationNo={
              auditApplication
                ? `GB-${auditApplication.applicationId || auditApplication.id}`
                : ""
            }
            submittedBy={
              auditApplication?.companyName ||
              auditApplication?.company_name ||
              "—"
            }
            submittedFor={
              auditApplication?.userName ||
              auditApplication?.user_name ||
              "—"
            }
            accessToken={authToken}
          />

          {/* Note Modal */}
          {showModal && (
            <>
              <div className="modal-backdrop show" onClick={() => setShowModal(false)}></div>
              <div className="modal show d-block" tabIndex="-1" role="dialog">
                <div className="modal-dialog reason-modal" role="document">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Note</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowModal(false)}
                        aria-label="Close"
                      ></button>
                    </div>
                    <div className="modal-body">
                      <p>{modalNote}</p>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowModal(false)}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={getTotalPages}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={pagination?.totalItems || filteredApplication.length}
          />
        </div>
      </div>
    </div>
  );
};

export default AppDiscrepancies;